/* eslint-disable no-param-reassign */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { toRailLabel } from 'lib/commandFormatRegistry';
import * as CommandHistoryService from 'services/command-history.service';

const norm = (val) => (val === undefined || val === null ? '' : String(val));

// A favourite is keyed by command + curve + structure (Story §6). The same
// curve+structure favourited under a different command is a separate key.
export const favouriteKey = (command, curve, structure) => [
    toRailLabel(command), norm(curve), norm(structure)
].join('||');

const keyOf = (entry) => favouriteKey(entry.command, entry.curve, entry.structure);

const commandHistoryStore = create(
    immer((set, get) => ({
        entries: [],
        loading: false,
        loaded: false,

        loadHistory: async () => {
            set((state) => { state.loading = true; });
            const res = await CommandHistoryService.getCommandHistory();
            set((state) => {
                state.loading = false;
                state.loaded = true;
                if (res?.status && Array.isArray(res?.data?.entity)) {
                    state.entries = res.data.entity;
                }
            });
        },

        // Adds a freshly captured entry to the top without a full refetch.
        addEntry: (entry) => set((state) => {
            if (!entry?.id) return;
            if (state.entries.some((e) => e.id === entry.id)) return;
            state.entries.unshift(entry);
        }),

        isFavourited: (key) => get().entries.some((e) => keyOf(e) === key && e.isFavourite),

        // Optimistically toggles every entry sharing the key, then persists.
        toggleFavourite: async ({ command, curve, structure }) => {
            const key = favouriteKey(command, curve, structure);
            const desired = !get().isFavourited(key);
            const ts = desired ? new Date().toISOString() : null;

            set((state) => {
                state.entries.forEach((e) => {
                    if (keyOf(e) === key) {
                        e.isFavourite = desired;
                        e.favouritedAt = ts;
                    }
                });
            });

            const res = await CommandHistoryService.toggleFavourite({
                command: toRailLabel(command), curve, structure, isFavourite: desired
            });

            if (!res?.status) {
                // revert on failure
                set((state) => {
                    state.entries.forEach((e) => {
                        if (keyOf(e) === key) {
                            e.isFavourite = !desired;
                            e.favouritedAt = !desired ? new Date().toISOString() : null;
                        }
                    });
                });
            }
        }
    }))
);

export default commandHistoryStore;
